import { isNil, omitBy } from "lodash";
import { nanoid } from "nanoid";

import * as dynamicTable from "@/apiv2/dynamic-tables/companySchemas";
import * as salesOrderApi from "@/apiv2/salesOrder";
import * as alertActions from "@/redux/alerts/alerts.actions";
import * as customerSelectors from "@/redux/customers/customers.selectors";
import * as dynamicTableViewsActions from "@/redux/dynamic-table-views/dynamic-table-views.actions";
import * as dynamicTableViewsSelectors from "@/redux/dynamic-table-views/dynamic-table-views.selectors";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";

import * as pageFilterActions from "./page-filter/page-filter.actions";
import * as pageFilterSelectors from "./page-filter/page-filter.selectors";
import * as actionTypes from "./sales-order.actionTypes";
import * as selectors from "./sales-order.selectors";
import * as searchFilterActions from "./search-filter/search-filter.actions";
import * as searchFilterSelectors from "./search-filter/search-filter.selectors";
import * as sortActions from "./sort/sort.actions";
import * as sortSelectors from "./sort/sort.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: actionTypes.RESET,
  });
};

export const setQueryKey = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_COMPANY_SCHEMAS,
    payload: data,
  });
};

export const setCompanySchemas = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_COMPANY_SCHEMAS,
    payload: data,
  });
};

export const setDefaultCompanySchemas = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_DEFAULT_COMPANY_SCHEMAS,
    payload: data,
  });
};

export const setCustomSchemas = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_CUSTOM_SCHEMAS,
    payload: data,
  });
};

export const setIsFetchingCompanySchemasStatus = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_FETCHING_COMPANY_SCHEMAS_STATUS,
    payload: status,
  });
};

export const setIsFetchingCustomSchemasStatus = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_FETCHING_CUSTOM_SCHEMAS_STATUS,
    payload: status,
  });
};

export const setSelectedRows = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_SELECTED_ROWS,
    payload: data,
  });
};

export const setActiveOrder = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_ACTIVE_ORDER,
    payload: data,
  });
};

export const setActiveColumn = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_ACTIVE_COLUMN,
    payload: data,
  });
};

export const setIsSavingCompanySchemasStatus = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_SAVING_COMPANY_SCHEMAS_STATUS,
    payload: status,
  });
};

export const setIsDuplicatingOrderStatus = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_DUPLICATING_ORDER_STATUS,
    payload: status,
  });
};

export const setIsDuplicateOrderAlertVisible = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_DUPLICATE_ORDER_ALERT_VISIBLE,
    payload: data,
  });
};

export const setIsExportingModalVisible = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_EXPORTING_MODAL_VISIBLE,
    payload: data,
  });
};

export const setIsDeleteCustomColumnModalVisible = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_DELETE_CUSTOM_COLUMN_MODAL_VISIBLE,
    payload: data,
  });
};

export const setIsDeletingColumnStatus = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_DELETING_COLUMN_STATUS,
    payload: status,
  });
};

export const setColumnAggregation = (columnId, type, enabled) => (dispatch, getState) => {
  const ALLOWED_TYPES = ["showTotal", "showAverage"];
  if (!ALLOWED_TYPES.includes(type)) { return; }

  const state = getState();
  const companySchemas = selectors.getCompanySchemas(state);
  const schemas = companySchemas?.toJS?.() ?? companySchemas ?? [];

  dispatch(
    setCompanySchemas(
      schemas.map((c) =>
        c.key === columnId ? { ...c, [type]: enabled } : c
      )
    )
  );
};

export const setPagination = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_PAGINATION,
    payload: data,
  });
};

export const resetPagination = () => (dispatch, getState) => {
  const state = getState();

  const pagination = selectors.getPagination(state);

  dispatch(setPagination({ page: 0, offset: 0, limit: pagination.limit }));
};

export const resetFilterSortAndRowsPerPage = () => (dispatch, getState) => {
  const state = getState();
  const pagination = selectors.getPagination(state);

  dispatch(pageFilterActions.setFilter([]));
  dispatch(sortActions.setSort([]));
  dispatch(searchFilterActions.setSearch(""));
  dispatch(setPagination({ page: 0, offset: 0, limit: pagination.limit }));
};

export const applyDefaultFieldsAndFiltersFromView = (view) => (dispatch, getState) => {
  const state = getState();
  const pagination = selectors.getPagination(state);
  const defaultCompanySchemas = selectors.getDefaultCompanySchemas(state);

  if (view.name === "None") {
    dispatch(setCompanySchemas(defaultCompanySchemas));
    return;
  }

  const fields = JSON.parse(view.fields);
  const filter = JSON.parse(view.filter);
  const sort = view.sort ? JSON.parse(view.sort) : [];
  const meta = view.meta ? JSON.parse(view.meta) : null;

  const metaAggregations = meta?.columnAggregations ?? {};

  dispatch(
    setCompanySchemas(
      defaultCompanySchemas.map((c) => {
        const field = fields.find((f) => f.key === c.key);
        const agg = metaAggregations[c.key];

        return {
          ...c,
          index: field ? field.index : c.index,
          width: field ? field.width : c.width,
          minWidth: field ? field.minWidth : c.minWidth,
          // a column the user cannot switch on stays hidden, whatever the view says
          isHidden: (c.canHide === false && c.isHidden) || (field ? field.isHidden : true),
          showTotal: agg?.showTotal ?? false,
          showAverage: agg?.showAverage ?? false,
        };
      })
    )
  );

  dispatch(setPagination({ page: 0, offset: 0, limit: meta?.rowsPerPage ?? pagination.limit }));
  dispatch(pageFilterActions.setFilter(filter));
  dispatch(sortActions.setSort(sort));
};

const EXCLUDE_COLUMNS_FOR_NO_PRICING_ROLE = [
  "cost",
  "margin",
  "estimated_margin",
  "total",
  "delivery_fee",
  "subtotal",
  "invoiced_to_date",
  "labour_actual",
  "labour_estimated",
  "to_be_invoiced",
  "total_linked_po_cost",
];
const EXCLUDE_COLUMNS_WITHOUT_ACCOUNTING_INTEGRATION = ["accounting_number"];

export const fetchCompanySchemas = () => async (dispatch, getState) => {
  try {
    const state = getState();

    const userRoleId = customerSelectors.getUserRole(state);
    const company = customerSelectors.companySelector(state);
    const currencySymbol = customerSelectors.getCurrencySymbol(state);
    const hasPriceAccess = userRoleId < 7;

    // A locked column is hidden and cannot be switched on, searched or sorted
    const isLockedColumn = (key) =>
      (EXCLUDE_COLUMNS_WITHOUT_ACCOUNTING_INTEGRATION.includes(key) &&
        !company.accountingSyncAllowed) ||
      (EXCLUDE_COLUMNS_FOR_NO_PRICING_ROLE.includes(key) && !hasPriceAccess);
    const lockColumn = (d) => ({
      ...d,
      isHidden: true,
      canHide: false,
      canSearch: false,
      canSort: false,
    });

    const selectedView = dynamicTableViewsSelectors.getSelectedView(state);
    const selectedViewFields = JSON.parse(selectedView?.fields || "[]");
    dispatch(setIsFetchingCompanySchemasStatus(STATUS.PENDING));

    const { data } = await dynamicTable.getCompanySchemas()();

    const schemas =
      selectedView.name === "None"
        ? data.map((d) => {
            if (isLockedColumn(d.key)) {
              return lockColumn(d);
            }
            return {
              ...d,
              canHide: d.isCustom ? true : d.canHide,
            };
          })
        : data.map((d) => {
            // Check the lock first: a view saved before a column existed has no entry for it,
            // and that column must still be locked for users who are not allowed to see it
            if (isLockedColumn(d.key)) {
              return lockColumn(d);
            }

            const field = selectedViewFields.find((f) => f.key === d.key);
            // if the field is not in the selected view, it means the field is hidden
            if (!field) {
              return {
                ...d,
                isHidden: true,
              };
            }

            return {
              ...d,
              isHidden: field.isHidden,
              index: field.index,
              width: field.width,
              minWidth: field.minWidth,
            };
          });

    let maxIndexOfNonNullValue = Math.max(
      ...schemas.map((item) => (item.index !== null ? item.index : -Infinity))
    );

    // Assign indexes to the schemas
    const formattedSchemas = schemas.map((item) => {
      if (!item.isHidden && item.index === null) {
        maxIndexOfNonNullValue += 1;
        return { ...item, index: maxIndexOfNonNullValue };
      }
      return item;
    });
    dispatch(
      setCompanySchemas(
        data
          .filter((d) => formattedSchemas.map((s) => s.key).includes(d.key))
          .map((d) => {
            const item = formattedSchemas.find((s) => s.key === d.key);
            return {
              ...d,
              name: d.isCustom ? d.name : d.name.replace("$", currencySymbol),
              isCustom: d.isCustom,
              width: item ? item.width : d.width,
              minWidth: item ? (item?.minWidth ?? d.width) : d.width,
              isHidden: item ? item.isHidden : d.isHidden,
              index: item ? item.index : d.index,
              canHide: item?.canHide ?? d.canHide,
              canSearch: item?.canSearch ?? d.canSearch,
              canSort: item?.canSort ?? d.canSort,
            };
          })
      )
    );
    dispatch(
      setDefaultCompanySchemas(
        data.map((d) => {
          const item = formattedSchemas.find((s) => s.key === d.key);
          return {
            ...d,
            name: d.isCustom ? d.name : d.name.replace("$", currencySymbol),
            isCustom: d.isCustom,
            width: item ? item.width : d.width,
            minWidth: item ? (item?.minWidth ?? d.width) : d.width,
            isHidden: item ? item.isHidden : d.isHidden,
            canHide: item?.canHide ?? d.canHide,
            canSearch: item?.canSearch ?? d.canSearch,
            canSort: item?.canSort ?? d.canSort,
          };
        })
      )
    );
    dispatch(setIsFetchingCompanySchemasStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingCompanySchemasStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const fetchCustomSchemas = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingCustomSchemasStatus(STATUS.PENDING));
    const { data } = await dynamicTable.getCustomSchemaTypes()();
    dispatch(setCustomSchemas(data));
    dispatch(setIsFetchingCustomSchemasStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingCustomSchemasStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

// Add Custom Fields
export const createCompanySchemas = (payload) => async (dispatch, getState) => {
  try {
    const state = getState();
    const companySchemas = selectors.getCompanySchemas(state);
    const defaultCompanySchemas = selectors.getDefaultCompanySchemas(state);
    const selectedView = dynamicTableViewsSelectors.getSelectedView(state);
    dispatch(setIsSavingCompanySchemasStatus(STATUS.PENDING));

    const { data } = await dynamicTable.postCompanySchemas(payload)();

    // Automatically saved new custom column to the selected view
    if (selectedView.name !== "None") {
      await dispatch(
        dynamicTableViewsActions.updateView({
          uid: selectedView.uid,
          payload: {
            fields: selectedView.fields
              ? JSON.stringify([
                  ...JSON.parse(selectedView.fields),
                  {
                    uid: data.uid,
                    index: data.index,
                    key: data.key,
                    name: data.name,
                    isHidden: data.isHidden,
                    width: data.width,
                    minWidth: data.width,
                    isCustom: data.isCustom,
                  },
                ])
              : "",
          },
        })
      );
    }

    dispatch(setDefaultCompanySchemas([...defaultCompanySchemas, data]));
    dispatch(setCompanySchemas([...companySchemas, data]));

    // append the newly added column to the lists of rows
    dispatch(setIsSavingCompanySchemasStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsSavingCompanySchemasStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

// Save Custom Fields
export const updateCompanySchemas =
  ({ uid, payload }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const companySchemas = selectors.getCompanySchemas(state);
      const defaultCompanySchemas = selectors.getDefaultCompanySchemas(state);

      dispatch(setIsSavingCompanySchemasStatus(STATUS.PENDING));
      const { data } = await dynamicTable.patchCompanySchemas({ uid, payload })();

      dispatch(
        setCompanySchemas(companySchemas.map((c) => (c.uid === uid ? { ...c, ...data } : { ...c })))
      );
      dispatch(
        setDefaultCompanySchemas(
          defaultCompanySchemas.map((c) => (c.uid === uid ? { ...c, ...data } : { ...c }))
        )
      );

      dispatch(setIsSavingCompanySchemasStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setIsSavingCompanySchemasStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

// Delete custom field
export const removeCustomSchema = (columnUid) => async (dispatch, getState) => {
  try {
    const state = getState();
    const companySchemas = selectors.getCompanySchemas(state);

    dispatch(setIsDeletingColumnStatus(STATUS.PENDING));
    await dynamicTable.deleteCustomSchema(columnUid)();
    dispatch(setIsDeletingColumnStatus(STATUS.FULFILLED));
    dispatch(setCompanySchemas(companySchemas.filter((c) => c.uid !== columnUid)));
  } catch (error) {
    dispatch(setIsDeletingColumnStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};
export const toggleColumn =
  ({ columns = {} }) =>
  async (dispatch, getState) => {
    const state = getState();
    const companySchemas = selectors.getCompanySchemas(state);

    // Get the current columns visibility
    const currentColumnsVisibility = companySchemas.reduce((acc, item) => {
      acc[item.key] = !item.isHidden;
      return acc;
    }, {});

    // Compare the current columns visibility with the columns that have been updated in visibility
    // If this contains 1 array string, it means the user clicks the individual column toggle
    // If this contains more than 1 array string, it means the user clicks the hide all or unhide all button
    const columnsWithUpdateInVisibility = Object.keys(columns).filter(
      (key) => columns[key] !== currentColumnsVisibility[key]
    );

    const updatedColumnsVisibility = Object.keys(columns).map((key) => ({
      key,
      isHidden: !columns[key],
    }));

    // if greater than one means the user click the hide all or unhide all button
    if (columnsWithUpdateInVisibility.length > 1) {
      dispatch(
        setCompanySchemas(
          companySchemas.map((c) => {
            const column = updatedColumnsVisibility.find((col) => col.key === c.key);

            if (column) {
              return {
                ...c,
                isHidden: column.isHidden,
                index: c.index,
              };
            }

            return c;
          })
        )
      );
    } else {
      const visibleColumns = updatedColumnsVisibility.filter((cs) => !cs.isHidden);

      dispatch(
        setCompanySchemas(
          companySchemas.map((c) => {
            const column = updatedColumnsVisibility.find((col) => col.key === c.key);
            if (c.key === columnsWithUpdateInVisibility[0]) {
              return {
                ...c,
                isHidden: column.isHidden,
                index: visibleColumns.length + 1,
              };
            }

            return c;
          })
        )
      );
    }

    // Backlog - Toggle column should work by using this api.
    // const { data } = await salesOrderApi.postSalesOrderToggleColumn({
    //   select_only: columnsWithUpdatedVisibility.filter((cs) => !cs.isHidden).map((c) => c.key),
    //   order_ids: orders.map((o) => o.uid),
    // })();

    // return snakecaseKeys(data);
  };

export const duplicateOrder =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    try {
      dispatch(setIsDuplicatingOrderStatus(STATUS.PENDING));
      const response = await salesOrderApi.postDuplicateOrder({ customerUid, orderUid })();
      const duplicatedOrderId = response.data;

      dispatch(setIsDuplicatingOrderStatus(STATUS.FULFILLED));
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          "Order duplicated successfully",
          true,
          "success",
          "Open new order",
          () => {
            window.location.href = `/customer/${customerUid}/order/${duplicatedOrderId}`;
          },
          "btn-open"
        )
      );
    } catch (error) {
      dispatch(setIsDuplicatingOrderStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export const archiveOrder =
  ({ customerUid, orderUid, refetchSalesOrder }) =>
  async (dispatch) => {
    try {
      await salesOrderApi.deleteOrder({ customerUid, orderUid })();

      if (refetchSalesOrder) {
        refetchSalesOrder();
      }

      await dispatch(
        alertActions.createAlertAction(
          nanoid(),
          "Order archived",
          false,
          "dark",
          "Undo",
          async () => {
            try {
              await salesOrderApi.unDeleteOrder({ customerUid, orderUid })();
              if (refetchSalesOrder) {
                refetchSalesOrder();
              }
            } catch (error) {
              handleError(error, dispatch);
            }
          }
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const unarchiveOrder =
  ({ customerUid, orderUid, refetchSalesOrder }) =>
  async (dispatch) => {
    try {
      await salesOrderApi.unDeleteOrder({ customerUid, orderUid })();

      if (refetchSalesOrder) {
        refetchSalesOrder();
      }

      await dispatch(
        alertActions.createAlertAction(
          nanoid(),
          "Order unarchived",
          false,
          "dark",
          "Undo",
          async () => {
            try {
              await salesOrderApi.deleteOrder({ customerUid, orderUid })();
              if (refetchSalesOrder) {
                refetchSalesOrder();
              }
            } catch (error) {
              handleError(error, dispatch);
            }
          }
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const bulkUpdateOrderStatus =
  ({ status }) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectedRows = selectors.getSelectedRows(state);

    const filteredCheckedOrders = selectedRows.filter(
      (order) => !["Draft", "Quote", "Archived"].includes(order.status.name)
    );

    if (["Picked Up", "Delivered", "Installed"].includes(status.name)) {
      await Promise.all(
        filteredCheckedOrders.map(async (order) => {
          await salesOrderApi.changeWorkflowOrder({
            order_uid: order.uid,
            is_received: status.name,
          })();
        })
      );
    } else {
      await Promise.all(
        filteredCheckedOrders.map(async (order) => {
          await salesOrderApi.moveWorkflowOrder({
            order_uid: order.uid,
            new_index: 0,
            new_column_uid: status.uid,
          })();

          await salesOrderApi.changeWorkflowOrder({ order_uid: order.uid, is_received: null })();
        })
      );
    }

    dispatch(
      alertActions.createAlertAction(
        nanoid(),
        `You have successfully changed the status on ${filteredCheckedOrders.length} order${
          filteredCheckedOrders.length > 1 ? "s" : ""
        }.`,
        true,
        "success"
      )
    );
  };

export const bulkArchiveOrder =
  ({ refetchSalesOrder }) =>
  async (dispatch, getState) => {
    const state = getState();
    const selectedRows = selectors.getSelectedRows(state);

    await Promise.all(
      selectedRows.map(async (order) =>
        salesOrderApi.deleteOrder({ customerUid: order.customer_id, orderUid: order.uid })()
      )
    );

    if (refetchSalesOrder) {
      refetchSalesOrder();
    }

    await dispatch(
      alertActions.createAlertAction(
        nanoid(),
        `You have successfully archived ${selectedRows.length} order${
          selectedRows.length > 1 ? "s" : ""
        }.`,
        false,
        "dark",
        "Undo",
        async () => {
          try {
            await Promise.all(
              selectedRows.map(async (order) =>
                salesOrderApi.unDeleteOrder({
                  customerUid: order.customer_id,
                  orderUid: order.uid,
                })()
              )
            );

            dispatch(
              alertActions.createAlertAction(
                nanoid(),
                `You have successfully restored ${selectedRows.length} order${
                  selectedRows.length > 1 ? "s" : ""
                }.`,
                true,
                "success"
              )
            );

            if (refetchSalesOrder) {
              refetchSalesOrder();
            }
          } catch (error) {
            handleError(error, dispatch);
          }
        }
      )
    );
  };

export const updateOrder =
  ({ customerUid, orderUid, payload }) =>
  async () => {
    return salesOrderApi.patchUpdateOrder({ customerUid, orderUid, payload })();
  };

export const exportOrders =
  (exportMode = "visible") =>
  async (dispatch, getState) => {
    const state = getState();
    const filter = pageFilterSelectors.getFilter(state);
    const sort = sortSelectors.getSort(state);
    const search = searchFilterSelectors.getSearch(state);
    const companySchemas = selectors.getCompanySchemas(state);

    dispatch(setIsExportingModalVisible(true));

    try {
      let selectOnly =
        exportMode === "all"
          ? companySchemas.map((c) => c.key)
          : companySchemas
              .filter((c) => !c.isHidden)
              .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
              .map((c) => c.key);

      selectOnly = ["order_number", ...selectOnly.filter((key) => key !== "order_number"), "custom_fields"];

      const { data: url } = await salesOrderApi.exportToFile({
        q: search,
        select_only: selectOnly,
        default_filters: omitBy(
          Object.fromEntries(
            filter
              .filter(({ id }) => !companySchemas.find((c) => c.key === id)?.isCustom)
              .map(({ id, values }) => [id, values])
          ),
          isNil
        ),
        custom_filters: omitBy(
          Object.fromEntries(
            filter
              .filter(({ id }) => companySchemas.find((c) => c.key === id)?.isCustom)
              .map(({ id, values }) => [id, values])
          ),
          isNil
        ),
        sorting: sort
          .filter((s) => s.id)
          .map((s) => ({ field_name: s.id, sort_by: s.desc ? "desc" : "asc" })),
      })();

      const link = document.createElement("a");
      document.body.appendChild(link);
      link.style.display = "none";
      link.href = url;
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsExportingModalVisible(false));
    }
  };
